import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ABC_AUTH_API } from 'app/core/auth-abc/auth-abc-api.interceptor';
import { ApiResponseModel } from 'app/core/models/apiResponse.model';
import { PaymentGatewayType } from 'app/modules/admin/reports/cash-free-report/cash-free-report.component';
import {
    AnalyticsReport,
    CashFreeReport,
    ChannelPartnerReport,
    FilmMakerReport,
    FilmPurchaseSummary,
    FilmReport,
    FreeContentReport,
    InAppPurchaseReport,
    InvoiceReport,
    TransactionReportData,
    UserReport,
    ViewsReport,
    WalletReport,
    WatchActivityResponse,
} from 'app/modules/admin/reports/report.interface';
import { Observable } from 'rxjs';

const httpOptions = {
    context: new HttpContext().set(ABC_AUTH_API, true),
};

@Injectable({
    providedIn: 'root',
})
export class ReportService {
    private readonly _httpClient = inject(HttpClient);
    private readonly base_url = 'admin';

    getInvoicePdf(orderId: string): Observable<Blob> {
        return this._httpClient.get(
            `${this.base_url}/promotional-report/invoice/${orderId}`,
            {
                ...httpOptions,
                responseType: 'blob',
            }
        );
    }

    getInvoiceReport(
        params: HttpParams | URLSearchParams
    ): Observable<ApiResponseModel<InvoiceReport[]>> {
        return this._httpClient.get<ApiResponseModel<InvoiceReport[]>>(
            `${this.base_url}/ticket-report?${params.toString()}`,
            httpOptions
        );
    }

    getPromotionalReport(
        params: HttpParams | URLSearchParams
    ): Observable<ApiResponseModel<InvoiceReport[]>> {
        return this._httpClient.get<ApiResponseModel<InvoiceReport[]>>(
            `${this.base_url}/promotional-report?${params.toString()}`,
            httpOptions
        );
    }

    getFilmPurchaseSummary(
        params: HttpParams | URLSearchParams
    ): Observable<ApiResponseModel<FilmPurchaseSummary[]>> {
        return this._httpClient.get<ApiResponseModel<FilmPurchaseSummary[]>>(
            `${this.base_url}/content-purchase-report?${params.toString()}`,
            httpOptions
        );
    }

    getCashFreeReport(
        params: URLSearchParams
    ): Observable<ApiResponseModel<CashFreeReport[]>> {
        return this._httpClient.get<ApiResponseModel<CashFreeReport[]>>(
            `${this.base_url}/credit/report?type=cashfree&${params.toString()}`,
            httpOptions
        );
    }

    getPaymentGatewayReport(
        params: URLSearchParams,
        type: PaymentGatewayType | null
    ): Observable<ApiResponseModel<CashFreeReport[]>> {
        const typeParam = type ? `type=${type}&` : '';
        return this._httpClient.get<ApiResponseModel<CashFreeReport[]>>(
            `${this.base_url}/credit/report?${typeParam}${params.toString()}`,
            httpOptions
        );
    }

    getWalletReport(
        params: HttpParams | URLSearchParams
    ): Observable<ApiResponseModel<WalletReport[]>> {
        return this._httpClient.get<ApiResponseModel<WalletReport[]>>(
            `${this.base_url}/viewer/transaction?${params.toString()}`,
            httpOptions
        );
    }

    getRewardTransactionReport(
        params: HttpParams | URLSearchParams,
        _type: string | null
    ): Observable<ApiResponseModel<TransactionReportData>> {
        return this._httpClient.get<ApiResponseModel<TransactionReportData>>(
            `${this.base_url}/viewer/reward/transactions?${params.toString()}`,
            httpOptions
        );
    }

    getAnalyticsReport(
        params: HttpParams | URLSearchParams
    ): Observable<ApiResponseModel<AnalyticsReport[]>> {
        return this._httpClient.get<ApiResponseModel<AnalyticsReport[]>>(
            `${this.base_url}/analytic-report?${params.toString()}`,
            httpOptions
        );
    }

    getFilmMakerReport(
        params: HttpParams | URLSearchParams
    ): Observable<ApiResponseModel<FilmMakerReport[]>> {
        return this._httpClient.get<ApiResponseModel<FilmMakerReport[]>>(
            `${this.base_url}/film-maker-stats?${params.toString()}`,
            httpOptions
        );
    }

    getFilmReport(
        params: HttpParams | URLSearchParams
    ): Observable<ApiResponseModel<FilmReport[]>> {
        return this._httpClient.get<ApiResponseModel<FilmReport[]>>(
            `${this.base_url}/film-detail/report?${params.toString()}`,
            httpOptions
        );
    }

    getUserReport(
        params: HttpParams | URLSearchParams
    ): Observable<ApiResponseModel<UserReport[]>> {
        return this._httpClient.get<ApiResponseModel<UserReport[]>>(
            `${this.base_url}/viewer-report?${params.toString()}`,
            httpOptions
        );
    }

    getViewsReport(
        params: HttpParams | URLSearchParams
    ): Observable<ApiResponseModel<ViewsReport[]>> {
        return this._httpClient.get<ApiResponseModel<ViewsReport[]>>(
            `${this.base_url}/film/wise/count?${params.toString()}`,
            httpOptions
        );
    }

    getChannelPartnerReport(
        params: HttpParams | URLSearchParams
    ): Observable<ApiResponseModel<ChannelPartnerReport[]>> {
        return this._httpClient.get<ApiResponseModel<ChannelPartnerReport[]>>(
            `${this.base_url}/cc/allowanceReport?${params.toString()}`,
            httpOptions
        );
    }

    getInAppPurchaseReport(
        params: HttpParams | URLSearchParams
    ): Observable<ApiResponseModel<InAppPurchaseReport[]>> {
        return this._httpClient.get<ApiResponseModel<InAppPurchaseReport[]>>(
            `${this.base_url}/credit/report?${params.toString()}`,
            httpOptions
        );
    }

    getFreeContentReport(
        params: URLSearchParams
    ): Observable<ApiResponseModel<FreeContentReport[]>> {
        return this._httpClient.get<ApiResponseModel<FreeContentReport[]>>(
            `${this.base_url}/free-content/report?${params.toString()}`,
            httpOptions
        );
    }

    getWatchActivityReport(
        params: URLSearchParams
    ): Observable<ApiResponseModel<WatchActivityResponse>> {
        return this._httpClient.get<ApiResponseModel<WatchActivityResponse>>(
            `${this.base_url}/watch-activity-report?${params.toString()}`,
            httpOptions
        );
    }
}
